import { useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { api } from "../lib/api";

/**
 * Minimal single-input waitlist form. Reused in Footer, sold-original states, and Originals page.
 * props: source (default 'general'), print_id (optional), variant ('footer' | 'inline'), placeholder, cta
 */
export default function WaitlistForm({ source = "general", print_id = null, variant = "inline", placeholder = "you@example.com", cta = "Join the list" }) {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setBusy(true);
        try {
            const r = await api.post("/waitlist", { email: email.trim(), source, print_id });
            if (r.data.already_on_list) toast.success("You're already on the list — thank you.");
            else toast.success("You're in. I'll be in touch when there's something worth sending.");
            setDone(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Please enter a valid email");
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <div className="inline-flex items-center gap-2 text-sm text-[#5A5A5A]" data-testid={`waitlist-thanks-${source}`}>
                <Check className="w-4 h-4 text-[#8C3A3A]" /> You're on the studio list.
            </div>
        );
    }

    if (variant === "footer") {
        return (
            <form onSubmit={submit} className="flex items-end gap-3 max-w-sm" data-testid="waitlist-footer-form">
                <div className="flex-1">
                    <label className="label-caps block mb-2">Studio List</label>
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={placeholder}
                        className="underline-input"
                        data-testid="waitlist-email-input"
                    />
                </div>
                <button
                    type="submit"
                    disabled={busy}
                    className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A] hover:text-[#8C3A3A] transition-colors pb-3 disabled:opacity-50 whitespace-nowrap"
                    data-testid="waitlist-submit"
                >
                    {busy ? "…" : cta}
                </button>
            </form>
        );
    }

    return (
        <form onSubmit={submit} className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3" data-testid={`waitlist-inline-form-${source}`}>
            <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={placeholder}
                className="underline-input flex-1 min-w-0"
                data-testid="waitlist-email-input"
            />
            <button
                type="submit"
                disabled={busy}
                className="border border-black/20 text-[#1A1A1A] px-6 py-3 rounded-full text-xs tracking-[0.2em] uppercase hover:bg-black/5 disabled:opacity-50 transition-colors whitespace-nowrap"
                data-testid="waitlist-submit"
            >
                {busy ? "Joining…" : cta}
            </button>
        </form>
    );
}
